<mat-expansion-panel
  class="cta-summary-expansion-panel cta-summary-expansion-panel-creator"
  hideToggle
  (expandedChange)="isExpanded.set($event)"
>
  <mat-expansion-panel-header collapsedHeight="auto" expandedHeight="auto">
    <div class="flex items-center gap-2 w-full min-w-0 px-3 py-2">
      <!-- Creator icon badge -->
      <app-icon-badge
        [content]="campaignCreator().creator?.icon ?? null"
        [fallbackText]="campaignCreator().creator?.name"
        [size]="32"
        [iconSize]="16"
        [borderRadius]="100"
      />

      <!-- Name + tag counts -->
      <div class="flex items-center gap-2 flex-1 min-w-0">
        <span class="text-sm font-medium truncate">{{ campaignCreator().creator?.name }}</span>

        @let counts = tagCounts();

        <div class="flex items-center gap-2 flex-wrap">
          @if (
            !counts.global &&
            !counts.globalCustomized &&
            !counts.shared &&
            !counts.sharedCustomized &&
            !counts.individual
          ) {
            <div class="flex items-center gap-1 text-xs text-shikenso-warning-base shrink-0">
              <app-icon src="icons/warning.svg" color="var(--shikenso-warning-base)" [size]="14" />
              <span>{{ 'pages.campaignForm.sections.creatorsAndCtas.noDeliverableGroups' | translate }}</span>
            </div>
          }

          @if (counts.global > 0) {
            <div class="flex items-center gap-1 text-xs text-shikenso-purple-base-60 shrink-0">
              <app-icon src="icons/world.svg" [size]="16" />
              <span>{{ counts.global }} {{ 'pages.campaignForm.sections.creatorsAndCtas.globalTag' | translate }}</span>
            </div>
          }

          @if (counts.globalCustomized > 0) {
            <div class="flex items-center gap-1 text-xs text-shikenso-purple-base-60 shrink-0">
              <app-icon src="icons/world.svg" [size]="16" />
              <span>
                {{ counts.globalCustomized }}
                {{ 'pages.campaignForm.sections.creatorsAndCtas.globalCustomizedTag' | translate }}
              </span>
            </div>
          }

          @if (counts.shared > 0) {
            <div class="flex items-center gap-1 text-xs text-shikenso-purple-base-60 shrink-0">
              <app-icon src="icons/creators.svg" [size]="16" />
              <span>{{ counts.shared }} {{ 'pages.campaignForm.sections.creatorsAndCtas.sharedTag' | translate }}</span>
            </div>
          }

          @if (counts.sharedCustomized > 0) {
            <div class="flex items-center gap-1 text-xs text-shikenso-purple-base-60 shrink-0">
              <app-icon src="icons/creators.svg" [size]="16" />
              <span>
                {{ counts.sharedCustomized }}
                {{ 'pages.campaignForm.sections.creatorsAndCtas.sharedCustomizedTag' | translate }}
              </span>
            </div>
          }

          @if (counts.individual > 0) {
            <div class="flex items-center gap-1 text-xs text-shikenso-purple-base-60 shrink-0">
              <app-icon src="icons/user-star.svg" [size]="16" />
              <span>
                {{ counts.individual }}
                {{ 'pages.campaignForm.sections.creatorsAndCtas.individualTag' | translate }}
              </span>
            </div>
          }
        </div>
      </div>

      <!-- Payout button -->
      @if (campaign()?.budget && campaign()?.currency) {
        @if (hasPayout()) {
          <button
            mat-flat-button
            type="button"
            class="mat-slim-button creator-cta-button !bg-shikenso-pink-100-24 shrink-0 !min-w-0"
            (click)="$event.stopPropagation(); addPayoutClick.emit()"
          >
            <div class="flex items-center gap-1">
              <app-icon alt="payout" src="icons/money.svg" [size]="12" />
              <span>{{ campaignCreator().cost ?? '' | numberSuffix }} {{ campaign()?.currency }}</span>
            </div>
          </button>
        } @else {
          <button
            mat-flat-button
            type="button"
            color="secondary"
            class="mat-slim-button creator-cta-button shrink-0 !min-w-0"
            (click)="$event.stopPropagation(); addPayoutClick.emit()"
          >
            <div class="flex items-center gap-1">
              <app-icon alt="payout" src="icons/money.svg" [size]="12" />
              {{ 'pages.campaignForm.sections.creatorsAndCtas.addPayout' | translate }}
            </div>
          </button>
        }
      }

      <!-- 3-dots menu -->
      <button mat-icon-button type="button" (click)="$event.stopPropagation()" [matMenuTriggerFor]="creatorMenu">
        <app-icon alt="menu" class="w-4 h-4" src="icons/dots.svg" />
      </button>
      <mat-menu #creatorMenu="matMenu">
        @if (campaign()?.budget && campaign()?.currency) {
          <button mat-menu-item type="button" (click)="addPayoutClick.emit()">
            {{ 'pages.campaignForm.sections.creatorsAndCtas.editPayout' | translate }}
          </button>
        }
        <button mat-menu-item type="button" (click)="deleteCreatorClick.emit()">
          {{ 'pages.campaignForm.sections.creatorsAndCtas.deleteCreator' | translate }}
        </button>
      </mat-menu>

      <!-- Chevron -->
      <app-icon
        [src]="isExpanded() ? 'icons/chevron-up.svg' : 'icons/chevron-down.svg'"
        [size]="16"
        class="text-shikenso-purple-base-60 shrink-0"
      />
    </div>
  </mat-expansion-panel-header>

  <!-- Panel body -->
  <div class="flex flex-col gap-3 px-4 py-3">
    <!-- Custom Briefing Note card -->
    <div
      class="flex items-center gap-3 w-full rounded-2xl border bg-shikenso-pink-100-12 border-shikenso-pink-100-50 px-3 py-4"
    >
      <app-icon-badge
        content="icons/notes.svg"
        backgroundColor="var(--shikenso-base-white)"
        color="var(--shikenso-pink-100)"
        [iconSize]="24"
        [size]="36"
      />

      <div class="flex flex-col flex-1 min-w-0">
        <div class="flex items-center gap-3">
          <span>{{ 'pages.campaignForm.sections.creatorsAndCtas.customBriefingNoteTitle' | translate }}</span>
          <span class="text-[10px] text-shikenso-pink-base bg-shikenso-base-white rounded-full px-3">
            {{ 'components.briefingNotes.optional' | translate }}
          </span>
        </div>
        <span class="text-shikenso-purple-base-60 text-xs">
          {{
            'pages.campaignForm.sections.creatorsAndCtas.customBriefingNoteDescription'
              | translate: { creatorName: campaignCreator().creator?.name }
          }}
        </span>
      </div>
      <button
        mat-flat-button
        type="button"
        color="secondary-white"
        class="mat-slim-plus-button"
        (click)="briefingNoteClick.emit()"
      >
        <div class="flex items-center gap-1">
          @if (!hasCreatorBriefingNote()) {
            <app-icon src="icons/plus.svg" [size]="16" />
          }

          {{
            hasCreatorBriefingNote()
              ? ('pages.campaignForm.sections.creatorsAndCtas.editNote' | translate)
              : ('pages.campaignForm.sections.creatorsAndCtas.addNote' | translate)
          }}
        </div>
      </button>
    </div>

    <!-- Individual Deliverables Group card -->
    <div
      class="flex items-center gap-3 w-full rounded-2xl border bg-shikenso-pink-100-12 border-shikenso-pink-100-50 px-3 py-4"
    >
      <app-icon-badge
        content="icons/user-star.svg"
        backgroundColor="var(--shikenso-base-white)"
        color="var(--shikenso-pink-100)"
        [iconSize]="24"
        [size]="36"
      />

      <div class="flex flex-col flex-1 min-w-0">
        <div class="flex items-center gap-3">
          <span>{{ 'pages.campaignForm.sections.creatorsAndCtas.individualDeliverablesGroupTitle' | translate }}</span>
          <span class="text-[10px] text-shikenso-pink-base bg-shikenso-base-white rounded-full px-3">
            {{ 'components.briefingNotes.optional' | translate }}
          </span>
        </div>
        <span class="text-shikenso-purple-base-60 text-xs">
          {{
            'pages.campaignForm.sections.creatorsAndCtas.individualDeliverablesGroupDescription'
              | translate: { creatorName: campaignCreator().creator?.name }
          }}
        </span>
      </div>
      <button
        mat-flat-button
        type="button"
        color="secondary-white"
        class="mat-slim-plus-button"
        (click)="addIndividualGroupClick.emit()"
      >
        <div class="flex items-center gap-1">
          <app-icon src="icons/plus.svg" [size]="16" />
          {{ 'pages.campaignForm.sections.creatorsAndCtas.addIndividualGroup' | translate }}
        </div>
      </button>
    </div>

    <!-- Per-creator CTA panels -->
    @let ctaList = campaignCreator().ctas ?? [];

    @if (ctaList.length) {
      <!-- Expand/Collapse All for CTAs -->
      <div class="flex justify-end">
        <button
          mat-flat-button
          type="button"
          color="secondary-gray"
          class="mat-tiny-button"
          (click)="expandCollapseAllCtas()"
        >
          <span class="text-xs">
            {{
              allCtasExpanded()
                ? ('pages.campaignForm.sections.creatorsAndCtas.collapseAll' | translate)
                : ('pages.campaignForm.sections.creatorsAndCtas.expandAll' | translate)
            }}
          </span>
        </button>
      </div>

      <div class="flex flex-col gap-3">
        @for (cta of ctaList; track cta.id) {
          @let creatorId = campaignCreator().creatorId ?? '';
          @let ctaInfo = ctaInfoMap().get(cta.id!) ?? { shareCount: 0, hasCustomValue: false };

          <app-cta-summary-panel
            panelClass="cta-summary-expansion-panel-gray"
            [creatorId]="creatorId"
            [cta]="cta"
            [getExistingDeliverablesCount]="getExistingDeliverablesCount()"
            [getExistingExecutionsCount]="getExistingExecutionsCount()"
            [getExistingKpisCount]="getExistingKpisCount()"
            [ctaShareCount]="ctaInfo.shareCount"
            [hasCustomValue]="ctaInfo.hasCustomValue"
            (addDeliverablesForCta)="
              addDeliverablesForCta.emit({ ctaId: $event, step: EDeliverableFlowStep.deliverable })
            "
            (addExecutionsForCta)="addDeliverablesForCta.emit({ ctaId: $event, step: EDeliverableFlowStep.execution })"
            (addKpisForCta)="addDeliverablesForCta.emit({ ctaId: $event, step: EDeliverableFlowStep.kpis })"
          >
            <div ctaMenu>
              <button mat-icon-button type="button" [matMenuTriggerFor]="ctaMenuRef">
                <app-icon alt="menu" class="w-4 h-4" src="icons/dots.svg" />
              </button>
              <mat-menu #ctaMenuRef="matMenu">
                <button mat-menu-item type="button" (click)="editCtaName.emit(cta.id!)">
                  {{ 'pages.campaignForm.sections.creatorsAndCtas.editName' | translate }}
                </button>
                <button mat-menu-item type="button" (click)="editCtaGroup.emit(cta.id!)">
                  {{ 'pages.campaignForm.sections.creatorsAndCtas.editIndividualGroup' | translate }}
                </button>
                <button mat-menu-item type="button" (click)="copyCtaForCreator.emit(cta.id!)">
                  {{
                    'pages.campaignForm.sections.creatorsAndCtas.copyCreator'
                      | translate: { name: campaignCreator().creator?.name }
                  }}
                </button>

                @if (cta.isAllCreators || ctaInfo.shareCount > 1) {
                  <button mat-menu-item type="button" (click)="copyCta.emit(cta.id!)">
                    {{
                      cta.isAllCreators
                        ? ('pages.campaignForm.sections.creatorsAndCtas.copyGlobal' | translate)
                        : ('pages.campaignForm.sections.creatorsAndCtas.copyShared' | translate)
                    }}
                  </button>
                }

                <span
                  [matTooltip]="
                    cta.isAllCreators
                      ? ('pages.campaignForm.sections.creatorsAndCtas.globalDeliverableGroupDeleteTooltip' | translate)
                      : ''
                  "
                  matTooltipPosition="before"
                >
                  <button
                    mat-menu-item
                    type="button"
                    [disabled]="!!cta.isAllCreators"
                    (click)="removeCtaFromCreator.emit({ ctaId: cta.id!, isAllCreators: !!cta.isAllCreators })"
                  >
                    {{ 'shared.delete' | translate }}
                  </button>
                </span>
              </mat-menu>
            </div>
          </app-cta-summary-panel>
        }
      </div>
    }
  </div>
</mat-expansion-panel>
